import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, FilePdf, FileDoc, FileText, Trash } from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";
import { getToken } from "@/lib/authToken";
const ICONS = {
  "application/pdf": { Icon: FilePdf, color: "text-red-600" },
  "application/msword": { Icon: FileDoc, color: "text-blue-600" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { Icon: FileDoc, color: "text-blue-600" },
};
const fmtSize = (b) => (b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB");
const fmtDate = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function MobileExpedienteDocs() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/expedientes/${id}/attachments`, { params: { kind: "docs" } });
      setItems(r.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar los documentos");
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      fd.append("kind", "doc");
      await api.post(`/expedientes/${id}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reload();
      toast.success("Documento subido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo");
    } finally {
      setUploading(false);
    }
  };

  const del = async (attId) => {
    if (!confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/expedientes/${id}/attachments/${attId}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  /**
   * Abre el archivo descargándolo como blob primero (evita popup blockers y
   * navegadores configurados para descargar PDFs).
   */
  const openAttachment = async (att) => {
    const token = getToken();
    const newWin = window.open("", "_blank");
    try {
      const res = await api.get(`/expedientes/${id}/attachments/${att.id}/file`, {
        params: { token },
        responseType: "blob",
      });
      const mime = att.mime_type || res.headers["content-type"] || "application/pdf";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      if (newWin && !newWin.closed) {
        newWin.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = att.filename || "documento";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      if (newWin && !newWin.closed) newWin.close();
      toast.error("No se pudo abrir el documento");
    }
  };

  return (
    <MobileShell title={`Documentos · ${items.length}`} backTo={`/m/expediente/${id}`}>
      <div className="px-3 pt-3">
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-white text-[14px] font-semibold py-3 rounded-md"
          data-testid="mobile-docs-upload"
        >
          <UploadSimple className="w-5 h-5" weight="bold" />
          Subir documento
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      {uploading && <div className="px-3 pt-2 text-[12px] text-muted text-center">Subiendo…</div>}

      <div className="px-3 pt-3 space-y-2">
        {items.length === 0 && !uploading && (
          <div className="text-center text-muted text-[12px] py-10">Sin documentos.</div>
        )}
        {items.map((it) => {
          const { Icon = FileText, color = "text-muted" } = ICONS[it.mime_type] || {};
          return (
            <div
              key={it.id}
              className="bg-white rounded-lg border border-[#E8EAED] p-3 flex items-center gap-3"
              data-testid={`mobile-doc-${it.id}`}
            >
              <Icon className={`w-7 h-7 ${color} shrink-0`} weight="duotone" />
              <button
                onClick={() => openAttachment(it)}
                className="flex-1 text-left min-w-0"
                data-testid={`mobile-open-doc-${it.id}`}
              >
                <div className="text-[13px] font-semibold truncate">{it.titulo_ia || it.filename}</div>
                <div className="text-[11px] text-muted">{fmtSize(it.size_bytes)} · {fmtDate(it.created_at)}</div>
              </button>
              {it.kind !== "parte" && (
                <button
                  onClick={() => del(it.id)}
                  className="p-2 text-muted hover:text-red-600"
                  aria-label="Eliminar"
                  data-testid={`mobile-del-doc-${it.id}`}
                >
                  <Trash className="w-4 h-4" weight="bold" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </MobileShell>
  );
}
